import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { AccountState } from '../../states/account.state';
import { InvestorProfiles } from 'src/app/models/account-status.model';
import {
  GetAccountStatusAction,
  RequestInvestorProfileAction,
} from 'src/app/actions/account.actions';
import { catchError, filter, map, retry, switchMap, take, timeout } from 'rxjs/operators';
import { SwalUtil } from 'src/app/util/swal-util';
import { EMPTY, of } from 'rxjs';
import { AccountApiService } from 'src/app/api/account-api.service';
import { IInversor } from 'src/app/models/investor.model';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class InvestorProfileGuard  {
  constructor(
    private store: Store,
    private router: Router,
    private accountApi: AccountApiService,
    private sanitizer: DomSanitizer
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const accountStatus = this.store.selectSnapshot(
      AccountState.getAccountStatus
    );

    if (accountStatus) {
      return this.validateAccountStatus(accountStatus);
    }

    return this.store.dispatch(new GetAccountStatusAction()).pipe(
      // Optional: retry the fetch a couple of times before failing
      retry(2),

      // After dispatching, wait until the state actually has accountStatus
      switchMap(() =>
        this.store.select(AccountState.getAccountStatus).pipe(
          filter(Boolean),
          take(1),
          timeout(10000),
          map((fetchedAccountStatus) =>
            this.validateAccountStatus(fetchedAccountStatus)
          )
        )
      ),

      catchError(() => {
        this.router.navigate(['/home']);
        return of(false);
      })
    );

    // if (accountStatus && accountStatus.inversorStatus) {
    //   const idLevel = Number(accountStatus.level.id);
    //   if (idLevel < 1) {
    //     Swal.fire(SwalUtil.getVerificarCuentaOptions()).then((result) => {
    //       if (result.isConfirmed) {
    //         this.router.navigate(['/verify-account']);
    //       }
    //     });
    //     return false;
    //   } else if (idLevel == 1) {
    //     Swal.fire(SwalUtil.getVerificarIdentidadOptions()).then((result) => {
    //       if (result.isConfirmed) {
    //         this.router.navigate(['/home']);
    //       }
    //     });
    //     return false;
    //   }
    //   const investorProfile = accountStatus.inversorStatus;
    //   switch (investorProfile) {
    //     case InvestorProfiles.accepted:
    //       return true;
    //     case InvestorProfiles.rejected:
    //       this.showRejectedMessage();
    //       return false;
    //     case InvestorProfiles.notSolicited:
    //       this.showNotSolicitedMessage();
    //       return false;
    //     case InvestorProfiles.pendingAuth:
    //       this.showPendingMessage();
    //       return false;
    //     case InvestorProfiles.pendingSign:
    //       this.showPendingSignatureMessage();
    //       return false;
    //   }
    // } else {
    //   this.router.navigate(['/home']);
    //   return false;
    // }
  }

  validateAccountStatus(accountStatus: any): boolean {
    const idLevel = Number(accountStatus.level.id);
    if (idLevel < 1) {
      Swal.fire(SwalUtil.getVerificarCuentaOptions()).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/verify-account']);
        }
      });
      return false;
    } else if (idLevel == 1) {
      Swal.fire(SwalUtil.getVerificarIdentidadOptions()).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/home']);
        }
      });
      return false;
    }
    const investorProfile = accountStatus.inversorStatus;
    switch (investorProfile) {
      case InvestorProfiles.accepted:
        return true;
      case InvestorProfiles.rejected:
        this.showRejectedMessage();
        return false;
      case InvestorProfiles.notSolicited:
        this.showNotSolicitedMessage();
        return false;
      case InvestorProfiles.pendingAuth:
        this.showPendingMessage();
        return false;
      case InvestorProfiles.pendingSign:
        this.showPendingSignatureMessage();
        return false;
    }

    this.router.navigate(['/home']);
    return false;
  }

  protected showRejectedMessage() {
    Swal.fire({
      title: 'Atención',
      text: 'Tu perfil inversor fue rechazado, podés comunicarte con nuestro soporte para obtener más información al respecto. ¿Te gustaría volver a solicitarlo?',
      icon: 'warning',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: ' Solicitar ',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0a6d37',
      reverseButtons: true,
      customClass: {
        confirmButton: 'swal-confirm-button',
        cancelButton: 'swal-cancel-button',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.store
          .dispatch(new RequestInvestorProfileAction())
          .pipe(
            take(1),
            catchError((err) => {
              SwalUtil.fireGenericError(
                'Error',
                'No se pudo enviar la solicitud. Intente más tarde.'
              );
              return EMPTY;
            })
          )
          .subscribe(() => {
            this.showPendingMessage(true);
          });
      } else {
        this.router.navigate(['/home']);
      }
    });
    // Swal.fire({
    //   title: 'Atención',
    //   text: 'No tenés permisos para invertir, si crees que es un error comunicate con Soporte.',
    //   icon: 'warning',
    //   showConfirmButton: true,
    //   confirmButtonText: 'Aceptar',
    //   confirmButtonColor: '#0a6d37',
    // }).then(() => {
    //   this.router.navigate(['/home']);
    // });
  }

  protected showNotSolicitedMessage() {
    Swal.fire({
      title: 'Atención',
      text: 'Todavía no tenes un perfil inversor, ¿te gustaría solicitarlo ahora?',
      icon: 'warning',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: ' Solicitar ',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0a6d37',
      reverseButtons: true,
      customClass: {
        confirmButton: 'swal-confirm-button',
        cancelButton: 'swal-cancel-button',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.store
          .dispatch(new RequestInvestorProfileAction())
          .pipe(
            take(1),
            catchError((err) => {
              SwalUtil.fireGenericError(
                'Error',
                'No se pudo enviar la solicitud. Intente más tarde.'
              );
              return EMPTY;
            })
          )
          .subscribe(() => {
            this.showPendingMessage(true);
          });
      } else {
        this.router.navigate(['/home']);
      }
    });
  }

  protected showPendingMessage(isSent: boolean = false) {
    Swal.fire({
      title: 'Atención',
      text: 'Estamos revisando tu solicitud para invertir, un asesor se comunicará contigo en breve.',
      icon: isSent ? 'success' : 'warning',
      showConfirmButton: true,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#0a6d37',
    }).then(() => {
      this.router.navigate(['/home']);
      return false;
    });
  }

  protected showPendingSignatureMessage() {
    this.accountApi.getInvestorState().subscribe({
      next: (data: IInversor) => {
        const byteCharacters = atob(data.archivo.base64);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const blobUrl = URL.createObjectURL(blob);

        Swal.fire({
          title: 'Perfil de Inversor',
          html: `<iframe src="${
            blobUrl + '#toolbar=0&navpanes=0&scrollbar=0'
          }" style="width:100%; height:350px;" frameborder="0"></iframe>`,
          width: 800,
          heightAuto: true,
          input: 'text',
          inputLabel:
            'Firma en el casillero debajo para aceptar los Terminos y Condiciones',
          inputPlaceholder: 'Ingrese su nombre completo',
          inputAttributes: {
            autocapitalize: 'on',
          },
          inputValidator: (value) => {
            if (!value) {
              return 'Ingrese su nombre completo';
            }
          },
          showCancelButton: true,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#0a6d37',
          cancelButtonText: 'Cancelar',
          cancelButtonColor: '#73867b',
          reverseButtons: true,
        }).then((confirm) => {
          const requestBody = {
            signature: confirm.value,
            version: data.declaracionJurada.version,
          };
          this.sendSignature(requestBody);
        });
      },
    });
  }

  private sendSignature(body: any) {
    this.accountApi.sendInvestorSignature(body).subscribe({
      next: () => {
        Swal.fire({
          title: 'Firma enviada',
          text: 'Ya podés comenzar a Invertir',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Invertir',
          confirmButtonColor: '#0a6d37',
        }).then(() => {
          this.router.navigate(['/invertir/cheques']);
        });
      },
      error: (error: HttpErrorResponse) => {
        console.log('error firma', error);
        if (error.status == 200) {
          Swal.fire({
            title: 'Firma enviada',
            text: 'Ya podés comenzar a Invertir',
            icon: 'success',
            showConfirmButton: true,
            confirmButtonText: 'Invertir',
            confirmButtonColor: '#0a6d37',
          }).then(() => {
            this.store.dispatch(new GetAccountStatusAction()).subscribe({
              next: () => {
                this.router.navigate(['/invertir/cheques']);
              },
            });
          });
        } else if (error.status == 409) {
          SwalUtil.fireGenericError(
            'Error',
            error.error[0].toUpperCase() + error.error.slice(1)
          );
        } else {
          SwalUtil.fireGenericError(
            'Error',
            'No se pudo enviar la firma. Intente más tarde.'
          );
        }
      },
    });
  }
}
